import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Table, Card, Button, Tag, Space, Modal, Form, Input, Switch, Tooltip, Popconfirm, Row, Col, Statistic, Select, Divider, message, Spin, DatePicker } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined, PlayCircleOutlined, PauseCircleOutlined, CheckCircleOutlined, ExclamationCircleOutlined, DatabaseOutlined, FilterOutlined, SearchOutlined } from '@ant-design/icons';
import { influxTargetsAPI, influxDataAPI } from '../services/api';
import socketService from '../services/socket';
import { toLocalTime } from '../utils/timezone';
import {
  getInfluxMonitorStatus,
  isActiveInfluxTarget,
  influxStatusTagColor,
  influxStatusLabel,
  normalizeInfluxStatus,
  isInfluxUpStatus,
} from '../utils/influxStatus';
import { parseApiData } from '../utils/apiResponse';
import { clearInfluxAckOnStatusChange } from '../utils/alertDedupe';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip as ChartTooltip, Legend, TimeScale } from 'chart.js';
import { Line } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, ChartTooltip, Legend, TimeScale);

const { RangePicker } = DatePicker;

const { TextArea } = Input;
const { Option } = Select;

const ANOMALY_METHODS = [
    { value: 'threshold', label: 'Threshold', description: 'Value exceeds min/max threshold' },
    { value: 'rate', label: 'Rate of Change', description: 'Sudden spike/drop compared to baseline' },
    { value: 'statistical', label: 'Statistical', description: 'Values outside N standard deviations' },
    { value: 'missing', label: 'Missing Data', description: 'Gaps in expected data stream' },
];

const DEVICE_COLORS = {
    'BIB_Lane1': 'rgb(75, 192, 192)',
    'BIB_Lane2': 'rgb(54, 162, 235)',
    'BIB_Lane3': 'rgb(255, 159, 64)',
    'BIB_COS': 'rgb(153, 102, 255)',
    'BIB_Default': 'rgb(201, 203, 207)',
};

const getDeviceColor = (device) => {
    return DEVICE_COLORS[device] || DEVICE_COLORS['BIB_Default'];
};

const InfluxDBTargets = () => {
    const [targets, setTargets] = useState([]);
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [modalMode, setModalMode] = useState('create');
    const [editingTarget, setEditingTarget] = useState(null);
    const [anomalyForm] = Form.useForm();
    const [selectedTarget, setSelectedTarget] = useState(null);
    const [dataModalVisible, setDataModalVisible] = useState(false);
    const [anomalyModalVisible, setAnomalyModalVisible] = useState(false);
    const [targetData, setTargetData] = useState([]);
    const [anomalyHistory, setAnomalyHistory] = useState([]);
    const [dataLoading, setDataLoading] = useState(false);
    const [influxConfig, setInfluxConfig] = useState(null);
    const [connectionStatus, setConnectionStatus] = useState(null);
    const [chartFilters, setChartFilters] = useState({ timeRange: '1h', devices: [], dateRange: null });
    const [filteredTableData, setFilteredTableData] = useState([]);
    const [availableDevices, setAvailableDevices] = useState([]);
    const [dataStats, setDataStats] = useState({ total: 0, anomalies: 0 });

    // Fetch influx targets
    const fetchTargets = useCallback(async () => {
        setLoading(true);
        try {
            const response = await influxTargetsAPI.getAll();
            const rows = parseApiData(response, []);
            if (!Array.isArray(rows)) {
                console.error('influx-targets: expected array, got', response);
                setTargets([]);
                message.error('Format response influx-targets tidak valid');
                return;
            }
            setTargets(rows);
            if (response?.success === false) {
                message.error(response.message || 'Gagal mengambil data influx targets');
            } else if (rows.length === 0) {
                console.warn('GET /api/influx-targets returned 0 rows', response);
            }
        } catch (error) {
            message.error('Gagal mengambil data influx targets — cek backend & login');
            console.error('Error fetching influx targets:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    // Fetch InfluxDB config status
    const fetchConfig = useCallback(async () => {
        try {
            const response = await influxTargetsAPI.getConfig();
            if (response.success) {
                setInfluxConfig(response.data);
            }
        } catch (error) {
            console.error('Error fetching influx config:', error);
        }
    }, []);

    // Test connection
    const testConnection = async () => {
        setConnectionStatus('testing');
        try {
            const response = await influxTargetsAPI.testConnection();
            setConnectionStatus(response.success ? 'success' : 'error');
            message.info(response.message);
        } catch (error) {
            setConnectionStatus('error');
            message.error('Connection test failed');
        }
    };

    useEffect(() => {
        fetchTargets();
        fetchConfig();
    }, [fetchTargets, fetchConfig]);

    useEffect(() => {
        const patchInfluxTarget = (influxTargetId, patch) => {
            const id = Number(influxTargetId);
            setTargets((prev) => prev.map((t) => {
                if (t.id !== id && t.id !== influxTargetId) return t;
                const status = patch.status ?? patch.last_status ?? t.last_status;
                const normalized = normalizeInfluxStatus(status) || status;
                const isRecovery = isInfluxUpStatus(normalized);
                return {
                    ...t,
                    ...patch,
                    last_status: normalized,
                    status: normalized,
                    last_error: isRecovery
                        ? null
                        : (patch.last_error ?? patch.errorMessage ?? patch.error ?? t.last_error),
                    last_check: patch.timestamp || patch.last_check || t.last_check,
                };
            }));
        };

        const handleInfluxStatusUpdate = (data) => {
            if (!data?.influxTargetId) return;
            clearInfluxAckOnStatusChange(data);
            const isUp = isInfluxUpStatus(data.status);
            patchInfluxTarget(data.influxTargetId, {
                last_status: data.status,
                status: data.status,
                last_error: isUp ? null : (data.errorMessage || data.error || null),
                timestamp: data.timestamp,
            });
        };

        const handleInfluxAlert = (data) => {
            if (!data?.targetId) return;
            patchInfluxTarget(data.targetId, {
                last_status: data.status || 'ANOMALY',
                status: data.status || 'ANOMALY',
                last_error: data.error || null,
                timestamp: data.timestamp,
            });
        };

        socketService.on('influx-status-update', handleInfluxStatusUpdate);
        socketService.on('influxdb-alert', handleInfluxAlert);

        return () => {
            socketService.off('influx-status-update', handleInfluxStatusUpdate);
            socketService.off('influxdb-alert', handleInfluxAlert);
        };
    }, []);

    // Filter table data based on chart filters
    useEffect(() => {
        if (!targetData || targetData.length === 0) {
            setFilteredTableData([]);
            return;
        }

        const now = new Date();
        let cutoffTime;
        let endTime = now;

        switch (chartFilters.timeRange) {
            case '5m': cutoffTime = new Date(now - 5 * 60 * 1000); break;
            case '15m': cutoffTime = new Date(now - 15 * 60 * 1000); break;
            case '30m': cutoffTime = new Date(now - 30 * 60 * 1000); break;
            case '45m': cutoffTime = new Date(now - 45 * 60 * 1000); break;
            case '1h': cutoffTime = new Date(now - 60 * 60 * 1000); break;
            case '6h': cutoffTime = new Date(now - 6 * 60 * 60 * 1000); break;
            case '24h': cutoffTime = new Date(now - 24 * 60 * 60 * 1000); break;
            case '7d': cutoffTime = new Date(now - 7 * 24 * 60 * 60 * 1000); break;
            case '30d': cutoffTime = new Date(now - 30 * 24 * 60 * 60 * 1000); break;
            case 'custom':
                if (chartFilters.dateRange && chartFilters.dateRange[0] && chartFilters.dateRange[1]) {
                    cutoffTime = chartFilters.dateRange[0].toDate();
                    endTime = chartFilters.dateRange[1].toDate();
                } else {
                    cutoffTime = null;
                }
                break;
            case 'all': cutoffTime = null; break;
            default: cutoffTime = new Date(now - 60 * 60 * 1000);
        }

        const filtered = targetData.filter(item => {
            const fields = parseFieldsJson(item.fields_json);
            const device = fields.device || 'Unknown';
            const itemTime = new Date(item.timestamp);

            if (cutoffTime && itemTime < cutoffTime) return false;
            if (endTime && itemTime > endTime) return false;
            if (chartFilters.devices.length > 0 && !chartFilters.devices.includes(device)) return false;
            return true;
        });

        setFilteredTableData(filtered);
    }, [targetData, chartFilters.timeRange, chartFilters.devices, chartFilters.dateRange]);

    // Open modal for creating new target
    const openCreateModal = () => {
        setModalMode('create');
        setEditingTarget(null);
        anomalyForm.resetFields();
        // Set default values for form
        anomalyForm.setFieldsValue({
            check_interval: 60,
            is_active: true,
            anomaly_method: 'threshold',
            anomaly_min: undefined,
            anomaly_max: undefined,
            anomaly_maxChangePercent: 50,
            anomaly_baselineWindow: 5,
            anomaly_stdDeviations: 3,
            anomaly_expectedInterval: 60000,
            anomaly_tolerance: 0.5,
        });
        setModalVisible(true);
    };

    // Open modal for editing target
    const openEditModal = (target) => {
        setModalMode('edit');
        setEditingTarget(target);

        let anomalyConfig = {};

        try {
            anomalyConfig = typeof target.anomaly_config === 'string' ? JSON.parse(target.anomaly_config) : target.anomaly_config || {};
        } catch (e) {}

        // Set all fields on anomalyForm (which is now the form used in the modal)
        anomalyForm.setFieldsValue({
            name: target.name,
            influx_query: target.influx_query,
            check_interval: target.check_interval,
            is_active: Boolean(target.is_active),
            email_notification: target.email_notification,
            anomaly_method: anomalyConfig.method || 'threshold',
            anomaly_min: anomalyConfig.params?.min,
            anomaly_max: anomalyConfig.params?.max,
            anomaly_maxChangePercent: anomalyConfig.params?.maxChangePercent,
            anomaly_baselineWindow: anomalyConfig.params?.baselineWindow,
            anomaly_stdDeviations: anomalyConfig.params?.stdDeviations,
            anomaly_expectedInterval: anomalyConfig.params?.expectedInterval,
            anomaly_tolerance: anomalyConfig.params?.tolerance,
        });

        setModalVisible(true);
    };

    // Handle form submission
    const handleSubmit = async (values) => {
        console.log('handleSubmit called with values:', values);
        try {
            // Validate form
            await anomalyForm.validateFields();
            console.log('Form validated');

            // Get anomaly values from form
            const anomalyValues = anomalyForm.getFieldsValue();
            console.log('Anomaly values from form:', anomalyValues);

            const anomalyConfig = {
                method: anomalyValues.anomaly_method || 'threshold',
                params: buildAnomalyParams(anomalyValues),
            };

            console.log('Anomaly config to save:', anomalyConfig);
            console.log('anomaly_min value:', anomalyValues.anomaly_min);
            console.log('anomaly_max value:', anomalyValues.anomaly_max);

            const payload = {
                name: values.name,
                influx_query: values.influx_query,
                check_interval: values.check_interval || 60,
                is_active: values.is_active ? 1 : 0,
                options: {},
                anomaly_config: anomalyConfig,
                email_notification: values.email_notification,
            };

            console.log('Payload:', payload);
            console.log('editingTarget.id:', editingTarget?.id);
            console.log('modalMode:', modalMode);

            if (modalMode === 'create') {
                console.log('Creating new target...');
                const response = await influxTargetsAPI.create(payload);
                console.log('Create response:', response);
                if (response.success) {
                    message.success('InfluxDB target berhasil dibuat');
                    setModalVisible(false);
                    fetchTargets();
                }
            } else {
                console.log('Updating target:', editingTarget.id);
                const response = await influxTargetsAPI.update(editingTarget.id, payload);
                console.log('Update response:', response);
                if (response.success) {
                    message.success('InfluxDB target berhasil diperbarui');
                    setModalVisible(false);
                    fetchTargets();
                } else {
                    message.error(response.message || 'Gagal memperbarui target');
                }
            }
        } catch (error) {
            console.error('Error in handleSubmit:', error);
            const errMsg = error.response?.data?.message || error.message || 'Unknown error';
            message.error('Gagal menyimpan influx target: ' + errMsg);
        }
    };

    // Build anomaly params based on method
    const buildAnomalyParams = (values) => {
        const method = values.anomaly_method || 'threshold';
        const params = {};

        console.log('buildAnomalyParams input:', values);
        console.log('buildAnomalyParams method:', method);
        console.log('anomaly_min:', values.anomaly_min, 'anomaly_max:', values.anomaly_max);

        switch (method) {
            case 'threshold':
                if (values.anomaly_min !== undefined && values.anomaly_min !== '' && values.anomaly_min !== null) {
                    params.min = parseFloat(values.anomaly_min);
                }
                if (values.anomaly_max !== undefined && values.anomaly_max !== '' && values.anomaly_max !== null) {
                    params.max = parseFloat(values.anomaly_max);
                }
                console.log('threshold params:', params);
                break;
            case 'rate':
                params.maxChangePercent = values.anomaly_maxChangePercent || 50;
                params.baselineWindow = values.anomaly_baselineWindow || 5;
                break;
            case 'statistical':
                params.stdDeviations = values.anomaly_stdDeviations || 3;
                break;
            case 'missing':
                params.expectedInterval = values.anomaly_expectedInterval || 60000;
                params.tolerance = values.anomaly_tolerance || 0.5;
                break;
            default:
                break;
        }

        if (values.field) params.field = values.field;

        console.log('buildAnomalyParams returning:', params);
        return params;
    };

    // Handle delete
    const handleDelete = async (id) => {
        try {
            const response = await influxTargetsAPI.delete(id);
            if (response.success) {
                message.success('InfluxDB target berhasil dihapus');
                fetchTargets();
            }
        } catch (error) {
            message.error('Gagal menghapus influx target');
        }
    };

    // Handle start/stop monitoring
    const handleStartStop = async (target, action) => {
        try {
            if (action === 'start') {
                await influxTargetsAPI.start(target.id);
                message.success('Monitoring started');
            } else {
                await influxTargetsAPI.stop(target.id);
                message.success('Monitoring paused');
            }
            fetchTargets();
        } catch (error) {
            message.error(`Failed to ${action} monitoring`);
        }
    };

    // Fetch data for a target
    const handleFetchData = async (target) => {
        try {
            const response = await influxTargetsAPI.fetch(target.id);
            if (response.success && response.data?.success !== false) {
                message.success(`Fetched ${response.data.count} data points`);
                fetchTargets();
            } else {
                message.warning(response.message || response.data?.error || 'Fetch failed (notification sent)');
            }
        } catch (error) {
            message.error(error.response?.data?.message || 'Failed to fetch data');
        }
    };

    // Handle anomaly check
    const handleAnomalyCheck = async (target) => {
        try {
            const response = await influxTargetsAPI.check(target.id);
            if (response.success && response.data?.success !== false) {
                const count = response.data.anomalies?.length || 0;
                message.success(`Found ${count} anomalies`);
            } else {
                message.warning(response.message || response.data?.error || 'Check failed (notification sent)');
            }
        } catch (error) {
            message.error(error.response?.data?.message || 'Failed to check anomalies');
        }
    };

    // Handle refresh all targets
    const handleRefreshAll = async () => {
        setRefreshing(true);
        try {
            const response = await influxTargetsAPI.refreshAll();
            if (response.success) {
                const results = response.data || [];
                const successful = results.filter(r => r.success).length;
                const failed = results.filter(r => !r.success).length;
                message.success(`Refreshed ${successful}/${results.length} targets${failed > 0 ? ` (${failed} failed)` : ''}`);
                fetchTargets();
            } else {
                message.error(response.message || 'Failed to refresh targets');
            }
        } catch (error) {
            message.error(error.response?.data?.message || 'Failed to refresh targets');
        } finally {
            setRefreshing(false);
        }
    };

    // Open data modal with chart
    const openDataModal = async (target) => {
        setSelectedTarget(target);
        setDataModalVisible(true);
        setDataLoading(true);

        try {
            const response = await influxDataAPI.getData(target.id, { limit: 500 });
            if (response.success) {
                const data = response.data || [];
                setTargetData(data);

                // Calculate stats
                const anomalyCount = data.filter(d => d.is_anomaly === 1).length;
                setDataStats({ total: data.length, anomalies: anomalyCount });

                // Extract unique devices for filter
                const devices = [...new Set(data.map(item => {
                    const fields = parseFieldsJson(item.fields_json);
                    return fields.device;
                }).filter(Boolean))];
                setAvailableDevices(devices);
                setChartFilters(prev => ({ ...prev, devices: [], timeRange: '1h' }));
            }
        } catch (error) {
            message.error('Failed to load data');
        } finally {
            setDataLoading(false);
        }
    };

    // Open anomaly history modal
    const openAnomalyModal = async (target) => {
        setSelectedTarget(target);
        setAnomalyModalVisible(true);
        setDataLoading(true);

        try {
            const response = await influxDataAPI.getAnomalies(target.id, { limit: 100 });
            if (response.success) {
                setAnomalyHistory(response.data || []);
            }
        } catch (error) {
            message.error('Failed to load anomaly history');
        } finally {
            setDataLoading(false);
        }
    };

    // Parse fields_json from stored data
    const parseFieldsJson = (fieldsJson) => {
        if (!fieldsJson) return {};
        try {
            return typeof fieldsJson === 'string' ? JSON.parse(fieldsJson) : fieldsJson;
        } catch (e) {
            return {};
        }
    };

    // Prepare chart data from target data
    const chartData = useMemo(() => {
        if (!targetData || targetData.length === 0) {
            return { datasets: [] };
        }

        // Get time range cutoff
        const now = new Date();
        let cutoffTime;
        let endTime = now;

        switch (chartFilters.timeRange) {
            case '5m': cutoffTime = new Date(now - 5 * 60 * 1000); break;
            case '15m': cutoffTime = new Date(now - 15 * 60 * 1000); break;
            case '30m': cutoffTime = new Date(now - 30 * 60 * 1000); break;
            case '45m': cutoffTime = new Date(now - 45 * 60 * 1000); break;
            case '1h': cutoffTime = new Date(now - 60 * 60 * 1000); break;
            case '6h': cutoffTime = new Date(now - 6 * 60 * 60 * 1000); break;
            case '24h': cutoffTime = new Date(now - 24 * 60 * 60 * 1000); break;
            case '7d': cutoffTime = new Date(now - 7 * 24 * 60 * 60 * 1000); break;
            case '30d': cutoffTime = new Date(now - 30 * 24 * 60 * 60 * 1000); break;
            case 'custom':
                if (chartFilters.dateRange && chartFilters.dateRange[0] && chartFilters.dateRange[1]) {
                    cutoffTime = chartFilters.dateRange[0].toDate();
                    endTime = chartFilters.dateRange[1].toDate();
                } else {
                    cutoffTime = null;
                }
                break;
            case 'all': cutoffTime = null; break;
            default: cutoffTime = new Date(now - 60 * 60 * 1000);
        }

        // Group data by device
        const deviceGroups = {};

        targetData.forEach(item => {
            const fields = parseFieldsJson(item.fields_json);
            const device = fields.device || 'Unknown';
            const timestamp = item.timestamp;
            const value = fields._value;

            // Apply time filter
            if (cutoffTime && new Date(timestamp) < cutoffTime) return;
            if (endTime && new Date(timestamp) > endTime) return;

            // Apply device filter
            if (chartFilters.devices.length > 0 && !chartFilters.devices.includes(device)) return;

            if (!deviceGroups[device]) {
                deviceGroups[device] = [];
            }

            deviceGroups[device].push({
                x: timestamp,
                y: value,
            });
        });

        // Sort each device's data by timestamp
        Object.values(deviceGroups).forEach(data => {
            data.sort((a, b) => new Date(a.x) - new Date(b.x));
        });

        // Create datasets
        const datasets = Object.entries(deviceGroups).map(([device, data]) => ({
            label: device,
            data: data,
            borderColor: getDeviceColor(device),
            backgroundColor: getDeviceColor(device).replace('rgb', 'rgba').replace(')', ', 0.1)'),
            tension: 0.1,
            pointRadius: 3,
            pointHoverRadius: 5,
            showLine: true,
        }));

        return { datasets };
    }, [targetData, chartFilters.timeRange, chartFilters.devices, chartFilters.dateRange]);

    // Calculate stats from filtered data
    const filteredStats = useMemo(() => {
        if (!targetData || targetData.length === 0) return { total: 0, anomalies: 0, min: '-', max: '-', avg: '-' };

        const now = new Date();
        let cutoffTime;
        let endTime = now;

        switch (chartFilters.timeRange) {
            case '5m': cutoffTime = new Date(now - 5 * 60 * 1000); break;
            case '15m': cutoffTime = new Date(now - 15 * 60 * 1000); break;
            case '30m': cutoffTime = new Date(now - 30 * 60 * 1000); break;
            case '45m': cutoffTime = new Date(now - 45 * 60 * 1000); break;
            case '1h': cutoffTime = new Date(now - 60 * 60 * 1000); break;
            case '6h': cutoffTime = new Date(now - 6 * 60 * 60 * 1000); break;
            case '24h': cutoffTime = new Date(now - 24 * 60 * 60 * 1000); break;
            case '7d': cutoffTime = new Date(now - 7 * 24 * 60 * 60 * 1000); break;
            case '30d': cutoffTime = new Date(now - 30 * 24 * 60 * 60 * 1000); break;
            case 'custom':
                if (chartFilters.dateRange && chartFilters.dateRange[0] && chartFilters.dateRange[1]) {
                    cutoffTime = chartFilters.dateRange[0].toDate();
                    endTime = chartFilters.dateRange[1].toDate();
                } else {
                    cutoffTime = null;
                }
                break;
            case 'all': cutoffTime = null; break;
            default: cutoffTime = new Date(now - 60 * 60 * 1000);
        }

        const values = [];
        let anomalyCount = 0;

        targetData.forEach(item => {
            const fields = parseFieldsJson(item.fields_json);
            const device = fields.device || 'Unknown';
            const timestamp = item.timestamp;
            const value = fields._value;
            const itemTime = new Date(timestamp);

            if (cutoffTime && itemTime < cutoffTime) return;
            if (endTime && itemTime > endTime) return;
            if (chartFilters.devices.length > 0 && !chartFilters.devices.includes(device)) return;
            if (value === undefined || value === null) return;

            values.push(value);
            if (item.is_anomaly === 1) anomalyCount++;
        });

        if (values.length === 0) return { total: 0, anomalies: anomalyCount, min: '-', max: '-', avg: '-' };

        const min = Math.min(...values);
        const max = Math.max(...values);
        const avg = (values.reduce((a, b) => a + b, 0) / values.length).toFixed(2);

        return { total: values.length, anomalies: anomalyCount, min: min.toFixed(2), max: max.toFixed(2), avg };
    }, [targetData, chartFilters.timeRange, chartFilters.devices, chartFilters.dateRange]);

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
            },
            title: {
                display: true,
                text: selectedTarget ? `Data for ${selectedTarget.name}` : 'InfluxDB Data',
            },
            tooltip: {
                mode: 'index',
                intersect: false,
            },
        },
        scales: {
            x: {
                type: 'time',
                time: {
                    displayFormats: {
                        minute: 'HH:mm',
                        hour: 'HH:mm',
                        day: 'MMM dd HH:mm',
                    }
                },
                title: {
                    display: true,
                    text: 'Time'
                }
            },
            y: {
                title: {
                    display: true,
                    text: 'Value'
                },
                beginAtZero: false
            }
        },
        interaction: {
            mode: 'nearest',
            axis: 'x',
            intersect: false
        },
        parsing: {
            yAxisKey: 'y',
            xAxisKey: 'x'
        }
    };

    // Render anomaly config form based on method
    const renderAnomalyConfig = () => {
        const method = anomalyForm.getFieldValue('anomaly_method') || 'threshold';

        return (
            <Form.Item label="Anomaly Method">
                <Select
                    value={method}
                    onChange={(value) => {
                        anomalyForm.setFieldsValue({ anomaly_method: value });
                    }}
                >
                    {ANOMALY_METHODS.map(m => (
                        <Option key={m.value} value={m.value}>{m.label}</Option>
                    ))}
                </Select>
            </Form.Item>
        );
    };

    const columns = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (text, record) => (
                <Space>
                    <DatabaseOutlined />
                    {text}
                </Space>
            ),
        },
        {
            title: 'Query',
            dataIndex: 'influx_query',
            key: 'influx_query',
            ellipsis: true,
            render: (text) => (
                <Tooltip title={text}>
                    <span style={{ maxWidth: 200, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {text}
                    </span>
                </Tooltip>
            ),
        },
        {
            title: 'Interval',
            dataIndex: 'check_interval',
            key: 'check_interval',
            render: (val) => `${val}s`,
        },
        {
            title: 'Monitor Status',
            key: 'last_status',
            render: (_, record) => {
                const monitorStatus = getInfluxMonitorStatus(record);
                return (
                    <Space size={4} direction="vertical" style={{ gap: 2 }}>
                        <Tag color={influxStatusTagColor(monitorStatus)}>
                            {influxStatusLabel(monitorStatus)}
                        </Tag>
                        <Tag color={record.is_active ? 'blue' : 'default'} style={{ margin: 0 }}>
                            {record.is_active ? 'Enabled' : 'Disabled'}
                        </Tag>
                    </Space>
                );
            },
        },
        {
            title: 'Last Check',
            dataIndex: 'last_check',
            key: 'last_check',
            render: (val) => val ? toLocalTime(val) : '-',
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space size="small">
                    <Tooltip title="Fetch Data">
                        <Button
                            icon={<ReloadOutlined />}
                            size="small"
                            onClick={() => handleFetchData(record)}
                        />
                    </Tooltip>
                    <Tooltip title="View Data">
                        <Button
                            icon={<SearchOutlined />}
                            size="small"
                            onClick={() => openDataModal(record)}
                        />
                    </Tooltip>
                    <Tooltip title="Anomaly History">
                        <Button
                            icon={<ExclamationCircleOutlined />}
                            size="small"
                            onClick={() => openAnomalyModal(record)}
                        />
                    </Tooltip>
                    <Tooltip title={record.is_active ? 'Pause' : 'Start'}>
                        <Button
                            icon={record.is_active ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
                            size="small"
                            onClick={() => handleStartStop(record, record.is_active ? 'stop' : 'start')}
                        />
                    </Tooltip>
                    <Tooltip title="Edit">
                        <Button
                            icon={<EditOutlined />}
                            size="small"
                            onClick={() => openEditModal(record)}
                        />
                    </Tooltip>
                    <Popconfirm
                        title="Delete this target?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Tooltip title="Delete">
                            <Button
                                icon={<DeleteOutlined />}
                                size="small"
                                danger
                            />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    const dataColumns = [
        { title: 'Timestamp', dataIndex: 'timestamp', key: 'timestamp', render: (val) => toLocalTime(val) },
        { title: 'Value', dataIndex: '_value', key: '_value', render: (val, record) => {
            const fields = parseFieldsJson(record.fields_json);
            return fields._value ?? '-';
        }},
        { title: 'Device', dataIndex: 'device', key: 'device', render: (val, record) => {
            const fields = parseFieldsJson(record.fields_json);
            return fields.device ?? '-';
        }},
        { title: 'Field', dataIndex: '_field', key: '_field', render: (val, record) => {
            const fields = parseFieldsJson(record.fields_json);
            return fields._field ?? '-';
        }},
        { title: 'Anomaly', dataIndex: 'is_anomaly', key: 'is_anomaly', render: (val) => (
            <Tag color={val ? 'red' : 'green'}>{val ? 'Yes' : 'No'}</Tag>
        )},
    ];

    const anomalyColumns = [
        { title: 'Timestamp', dataIndex: 'timestamp', key: 'timestamp', render: (val) => toLocalTime(val) },
        { title: 'Value', dataIndex: '_value', key: '_value', render: (val, record) => {
            const fields = parseFieldsJson(record.fields_json);
            return fields._value ?? '-';
        }},
        { title: 'Device', dataIndex: 'device', key: 'device', render: (val, record) => {
            const fields = parseFieldsJson(record.fields_json);
            return fields.device ?? '-';
        }},
        { title: 'Reason', dataIndex: 'anomaly_reason', key: 'anomaly_reason', ellipsis: true },
    ];

    const activeList = targets.filter(isActiveInfluxTarget);
    const influxAnomalyCount = activeList.filter((t) => getInfluxMonitorStatus(t) === 'ANOMALY').length;
    const influxDownCount = activeList.filter((t) => {
        const s = getInfluxMonitorStatus(t);
        return s === 'DOWN' || s === 'ERROR';
    }).length;
    const influxOkCount = activeList.filter((t) => getInfluxMonitorStatus(t) === 'UP').length;

    return (
        <div>
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={12} md={4}>
                    <Card>
                        <Statistic title="Total InfluxDB Targets" value={targets.length} />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={4}>
                    <Card>
                        <Statistic
                            title="Active Targets"
                            value={activeList.length}
                            valueStyle={{ color: '#3f8600' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={4}>
                    <Card>
                        <Statistic
                            title="Status OK"
                            value={influxOkCount}
                            valueStyle={{ color: '#3f8600' }}
                            prefix={<CheckCircleOutlined />}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={4}>
                    <Card>
                        <Statistic
                            title="ANOMALY"
                            value={influxAnomalyCount}
                            valueStyle={{ color: influxAnomalyCount > 0 ? '#fa8c16' : '#d9d9d9' }}
                            prefix={<ExclamationCircleOutlined />}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={4}>
                    <Card>
                        <Statistic
                            title="DOWN / ERROR"
                            value={influxDownCount}
                            valueStyle={{ color: influxDownCount > 0 ? '#cf1322' : '#d9d9d9' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={4}>
                    <Card>
                        <Statistic
                            title="InfluxDB Connection"
                            value={influxConfig?.isConfigured ? 'Configured' : 'Not Configured'}
                            valueStyle={{ color: influxConfig?.isConfigured ? '#3f8600' : '#cf1322' }}
                        />
                    </Card>
                </Col>
            </Row>

            <Card
                title="InfluxDB Targets"
                extra={
                    <Space>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={handleRefreshAll}
                            loading={refreshing}
                        >
                            Refresh All
                        </Button>
                        <Button
                            icon={<CheckCircleOutlined />}
                            onClick={testConnection}
                            loading={connectionStatus === 'testing'}
                        >
                            Test Connection
                        </Button>
                        <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
                            Add InfluxDB Target
                        </Button>
                    </Space>
                }
            >
                <Table
                    columns={columns}
                    dataSource={targets}
                    rowKey="id"
                    loading={loading}
                    pagination={{ pageSize: 10 }}
                    locale={{
                        emptyText: (
                            <div style={{ padding: 24 }}>
                                <div>Tidak ada data</div>
                                <div style={{ fontSize: 12, color: '#888', marginTop: 8 }}>
                                    Buka DevTools → Network → cari request <strong>influx-targets</strong> (bukan alerts).
                                    Jika <code>data: []</code>, backend terhubung ke DB kosong — cek DATABASE_URL.
                                </div>
                            </div>
                        ),
                    }}
                />
            </Card>

            <Modal
                title={modalMode === 'create' ? 'Add InfluxDB Target' : 'Edit InfluxDB Target'}
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                footer={null}
                width={700}
            >
                <Form form={anomalyForm} layout="vertical" onFinish={handleSubmit}>
                    <Form.Item
                        name="name"
                        label="Target Name"
                        rules={[{ required: true, message: 'Please enter target name' }]}
                    >
                        <Input placeholder="e.g., Server CPU Monitoring" />
                    </Form.Item>

                    <Form.Item
                        name="influx_query"
                        label="InfluxDB Query (Flux)"
                        rules={[{ required: true, message: 'Please enter InfluxDB query' }]}
                    >
                        <TextArea rows={4} placeholder="e.g., from(bucket: &quot;my-bucket&quot;) |> range(start: -1h) |> ..." />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="check_interval" label="Check Interval (seconds)" initialValue={60}>
                                <Input type="number" min={10} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="is_active" label="Active" valuePropName="checked" initialValue={true}>
                                <Switch />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Divider>Anomaly Detection Configuration</Divider>

                    <Form.Item name="anomaly_method" label="Anomaly Method">
                        <Select
                            value={anomalyForm.getFieldValue('anomaly_method') || 'threshold'}
                            onChange={(value) => anomalyForm.setFieldsValue({ anomaly_method: value })}
                        >
                            {ANOMALY_METHODS.map(m => (
                                <Option key={m.value} value={m.value}>{m.label}</Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item noStyle shouldUpdate={(prev, curr) => prev.anomaly_method !== curr.anomaly_method}>
                        {() => {
                            const method = anomalyForm.getFieldValue('anomaly_method') || 'threshold';

                            if (method === 'threshold') {
                                return (
                                    <Row gutter={16}>
                                        <Col span={12}>
                                            <Form.Item name="anomaly_min" label="Min Threshold">
                                                <Input type="number" placeholder="Optional" />
                                            </Form.Item>
                                        </Col>
                                        <Col span={12}>
                                            <Form.Item name="anomaly_max" label="Max Threshold">
                                                <Input type="number" placeholder="Optional" />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                );
                            }

                            if (method === 'rate') {
                                return (
                                    <Row gutter={16}>
                                        <Col span={12}>
                                            <Form.Item name="anomaly_maxChangePercent" label="Max Change (%)" initialValue={50}>
                                                <Input type="number" />
                                            </Form.Item>
                                        </Col>
                                        <Col span={12}>
                                            <Form.Item name="anomaly_baselineWindow" label="Baseline Window" initialValue={5}>
                                                <Input type="number" />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                );
                            }

                            if (method === 'statistical') {
                                return (
                                    <Row gutter={16}>
                                        <Col span={12}>
                                            <Form.Item name="anomaly_stdDeviations" label="Std Deviations" initialValue={3}>
                                                <Input type="number" />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                );
                            }

                            if (method === 'missing') {
                                return (
                                    <Row gutter={16}>
                                        <Col span={12}>
                                            <Form.Item name="anomaly_expectedInterval" label="Expected Interval (ms)" initialValue={60000}>
                                                <Input type="number" />
                                            </Form.Item>
                                        </Col>
                                        <Col span={12}>
                                            <Form.Item name="anomaly_tolerance" label="Tolerance (%)" initialValue={0.5}>
                                                <Input type="number" step="0.1" />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                );
                            }

                            return null;
                        }}
                    </Form.Item>

                    <Form.Item>
                        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                            <Button onClick={() => setModalVisible(false)}>Cancel</Button>
                            <Button type="primary" htmlType="submit">
                                {modalMode === 'create' ? 'Create' : 'Update'}
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title={`Data for ${selectedTarget?.name}`}
                open={dataModalVisible}
                onCancel={() => {
                    setDataModalVisible(false);
                    setTargetData([]);
                }}
                footer={null}
                width={1100}
            >
                <Spin spinning={dataLoading}>
                    <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                        <Col xs={12} sm={6}>
                            <Card size="small">
                                <Statistic title="Total Points" value={filteredStats.total} />
                            </Card>
                        </Col>
                        <Col xs={12} sm={6}>
                            <Card size="small">
                                <Statistic title="Anomalies" value={filteredStats.anomalies} valueStyle={{ color: filteredStats.anomalies > 0 ? '#cf1322' : '#3f8600' }} />
                            </Card>
                        </Col>
                        <Col xs={12} sm={6}>
                            <Card size="small">
                                <Statistic title="Min" value={filteredStats.min} />
                            </Card>
                        </Col>
                        <Col xs={12} sm={6}>
                            <Card size="small">
                                <Statistic title="Max" value={filteredStats.max} />
                            </Card>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Card size="small">
                                <Statistic title="Avg" value={filteredStats.avg} />
                            </Card>
                        </Col>
                    </Row>

                    <Card
                        size="small"
                        title={
                            <Space>
                                <FilterOutlined />
                                Filters
                            </Space>
                        }
                        extra={
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={() => setChartFilters({ timeRange: '1h', devices: [], dateRange: null })}
                                size="small"
                            >
                                Reset
                            </Button>
                        }
                        style={{ marginBottom: 16 }}
                    >
                        <Row gutter={[16, 16]}>
                            <Col xs={24} sm={12} md={8}>
                                <Form.Item label="Time Range" style={{ marginBottom: 8 }}>
                                    <Select
                                        value={chartFilters.timeRange}
                                        onChange={(value) => setChartFilters(prev => ({ ...prev, timeRange: value, dateRange: value !== 'custom' ? null : prev.dateRange }))}
                                        style={{ width: '100%' }}
                                    >
                                        <Option value="5m">Last 5 Minutes</Option>
                                        <Option value="15m">Last 15 Minutes</Option>
                                        <Option value="30m">Last 30 Minutes</Option>
                                        <Option value="45m">Last 45 Minutes</Option>
                                        <Option value="1h">Last 1 Hour</Option>
                                        <Option value="6h">Last 6 Hours</Option>
                                        <Option value="24h">Last 24 Hours</Option>
                                        <Option value="7d">Last 7 Days</Option>
                                        <Option value="30d">Last 30 Days</Option>
                                        <Option value="custom">Custom Range</Option>
                                    </Select>
                                </Form.Item>
                                {chartFilters.timeRange === 'custom' && (
                                    <Form.Item label="Date Range" style={{ marginBottom: 0 }}>
                                        <RangePicker
                                            showTime
                                            format="YYYY-MM-DD HH:mm:ss"
                                            style={{ width: '100%' }}
                                            value={chartFilters.dateRange}
                                            onChange={(dateRange) => setChartFilters(prev => ({ ...prev, dateRange }))}
                                        />
                                    </Form.Item>
                                )}
                            </Col>
                            <Col xs={24} sm={12} md={16}>
                                <Form.Item label="Device Filter" style={{ marginBottom: 8 }}>
                                    <Select
                                        mode="multiple"
                                        placeholder="All devices"
                                        value={chartFilters.devices}
                                        onChange={(value) => setChartFilters(prev => ({ ...prev, devices: value }))}
                                        style={{ width: '100%' }}
                                        allowClear
                                    >
                                        {availableDevices.map(d => (
                                            <Option key={d} value={d}>{d}</Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>
                    </Card>

                    <div style={{ height: 400, marginBottom: 16 }}>
                        {targetData.length > 0 ? (
                            <Line data={chartData} options={chartOptions} />
                        ) : (
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#999' }}>
                                No data available. Click "Fetch Data" first.
                            </div>
                        )}
                    </div>
                    <Table
                        columns={dataColumns}
                        dataSource={filteredTableData}
                        rowKey="id"
                        pagination={{ pageSize: 10 }}
                        size="small"
                    />
                </Spin>
            </Modal>

            <Modal
                title={`Anomaly History for ${selectedTarget?.name}`}
                open={anomalyModalVisible}
                onCancel={() => {
                    setAnomalyModalVisible(false);
                    setAnomalyHistory([]);
                }}
                footer={null}
                width={900}
            >
                <Table
                    columns={anomalyColumns}
                    dataSource={anomalyHistory}
                    rowKey="id"
                    loading={dataLoading}
                    pagination={{ pageSize: 10 }}
                />
            </Modal>
        </div>
    );
};

export default InfluxDBTargets;
